import { serviceSaveLayer } from "../../../api/services/layer";
import Type from "../../Types";
import { getEditedGeoJSON } from "../../geojson/GetEditedGeojson";
import { diffGeoJSON } from "../../geojson/GetDiffrentGeojson";
import { showToast, bboxCornersLonLatArrays, createGeoJSONFile, getGeometryTypeGeojson, removeFileExtension } from "../../Utility";

export async function saveLayerToServerUnified({
  layer,
  userData,
  pointCloudStyle,
  description = "",
  createFileBaseName,
  createFileEpoch = Math.floor(Date.now() / 1000),
  validateNotEmpty = false,
  emptyMessage = "The layer is empty — there are no features to save.",
  attachFile = "auto", // "auto" | "always" | "never"
  sendLayerId = true,
  layerType = null,
  isSendChangeset = true,
}) {
  const formData = new FormData();

  const isGeo = layer?.type === Type.ASSET.GEOJSON || layer?.type === Type.ASSET.GEOJSON_IMAGE;
  const isAerial = layer?.type === Type.ASSET.IMAGERY || layer?.type === "aerial_imagery";

  const getLayerType = () => {
    if (layer?.is_load_from_database) return "postgis";
    if (layer?.type === Type.ASSET.OSM) return "osm";
    if (layer?.type === Type.ASSET.GEOJSON || layer?.type === Type.ASSET.GEOJSON_IMAGE) return "geojson";
    if (layer?.type === Type.ASSET.THREEDTILES) return "3d_tiles";
    if (isAerial) return "aerial_imagery";
    return "camera";
  };

  if (isGeo) {
    let geojson = layer.type === Type.ASSET.GEOJSON ? getEditedGeoJSON(layer) : layer.geojson;

    if (validateNotEmpty) {
      const hasFeatures = (() => {
        if (!geojson) return false;
        const t = geojson.type;
        if (t === "FeatureCollection") return Array.isArray(geojson.features) && geojson.features.length > 0;
        if (t === "Feature") return !!geojson.geometry && geojson.geometry.type !== "GeometryCollection";
        if (t === "GeometryCollection") return Array.isArray(geojson.geometries) && geojson.geometries.length > 0;
        return !!t;
      })();

      if (!hasFeatures) {
        showToast(emptyMessage, Type.NOTIFICATION.WARNING);
        return null;
      }
    }

    const changeset = isSendChangeset ? diffGeoJSON(layer.original_geojson, getEditedGeoJSON(layer)) : [];
    const file = createGeoJSONFile(geojson, createFileBaseName, createFileEpoch);

    const willAttachFile = attachFile === "always" ? true : attachFile === "never" ? false : !layer?.layer_id || changeset.length != 0;

    if (willAttachFile) {
      formData.append("file", file);
    }

    const bbox = bboxCornersLonLatArrays(geojson);

    const layer_style = {
      style_stroke_width: layer.style_stroke_width ? layer.style_stroke_width : null,
      style_fill_color: layer.style_fill_color ? layer.style_fill_color : null,
      style_outline_color: layer.style_outline_color ? layer.style_outline_color : null,
      style_type: layer.style_type ? layer.style_type.toLowerCase() : "solid",
      style_pattern: layer.style_pattern ? layer.style_pattern : "solid",
      style_fill_pattern: layer.style_fill_pattern ? layer.style_fill_pattern : "",
      style_invisible_default: layer.style_invisible_default ? layer.style_invisible_default : false,
      label_text_size: layer.label_text_size ? layer.label_text_size : null,
      label_visible: layer.label_visible ? layer.label_visible : false,
      label_offset: layer.label_offset ? layer.label_offset : null,
      label_text_color: layer.label_text_color ? layer.label_text_color : null,
      label_interval: layer.label_interval ? layer.label_interval : null,
      selected_tags_for_label: layer.selected_tags_for_label ? layer.selected_tags_for_label : null,
      is_disable_loading: layer.isDisableLoading,
      db_schema: layer.sql_schema,
      db_table: layer.sql_table,
      db_geometry_field: layer.sql_geometry_field,
      db_db: layer.sql_db,
      db_where: layer.sql_where,
      filters: layer.filters ? layer.filters : [],
    };

    const computedLayerName = willAttachFile ? removeFileExtension(file.name) : layer.file_name;

    const layer_type = layerType ? layerType : getLayerType();
    let geometry_type = layer.type === Type.ASSET.GEOJSON ? getGeometryTypeGeojson(geojson) : "";

    formData.append(
      "data",
      JSON.stringify({
        layer_name: computedLayerName,
        layer_style,
        layer_type,
        geometry_type,
        description,
        username: userData.user.username,
        bbox: Array.isArray(bbox) ? bbox : JSON.stringify(bbox),
        changeset: JSON.stringify(changeset),
        change: changeset.length == 0 ? false : true,
      }),
    );
  } else if (layer?.type === Type.ASSET.THREEDTILES) {
    formData.append(
      "data",
      JSON.stringify({
        layer_name: layer.file_name,
        layer_style: pointCloudStyle,
        url: layer.url,
        geometry_type: "mixed",
        layer_type: "3d_tiles",
        description,
        username: userData.user.username,
        bbox: [],
      }),
    );
  } else if (isAerial) {
    const layer_type = layerType ? layerType : getLayerType();

    // Save minimal imagery style
    const layer_style = {
      style_invisible_default: !layer?.visible,
      style_alpha: layer?.imagery?.alpha ?? layer?.style_alpha ?? 1,
      max_tile_errors: layer?.imagery?.maxTileErrors ?? layer?.maxTileErrors ?? 5,
      is_disable_loading: layer?.isDisableLoading,
      url: layer?.imagery?.source ?? layer?.url ?? "",
    };

    formData.append(
      "data",
      JSON.stringify({
        layer_name: removeFileExtension(layer.file_name),
        layer_style,

        geometry_type: "raster",
        layer_type: layer_type,
        description,
        username: userData.user.username,
        bbox: [],
      }),
    );
  }

  if (sendLayerId && layer?.layer_id) {
    formData.append("layer_id", layer.layer_id);
  }

  const res = await serviceSaveLayer(formData);
  return res;
}
